import Skeleton from '@mui/material/Skeleton';
import { Inbox, RefreshCw } from 'lucide-react';
import './AssignedTicketsPanel.scss';

const PRIORITY_COLOR_VAR = {
    high: '--dbp-priority-high',
    normal: '--dbp-priority-normal',
    medium: '--dbp-priority-normal',
    low: '--dbp-priority-low',
};

const formatPriority = (priority) => {
    const trimmed = (priority || '').trim();
    if (!trimmed) return '';
    return trimmed[0].toUpperCase() + trimmed.slice(1).toLowerCase();
};

// Matches the label chip convention used everywhere else labels are shown in this app
// (Agile's LabelClickUpMenu getBaseColor): a fixed, hand-picked palette, a full-string
// hash of name+id (not just the first character, which collides for e.g. "Urgent" and
// "UI Bug"), and an explicit fallback for a blank/missing name.
const LABEL_COLORS = ['#2784B9', '#40c47d', '#FDB16C', '#b06060', '#f77474', '#6080b0', '#9a60b0', '#6cb060',
    '#b0ae60', '#65412b', '#cc9963', '#e1bd83', '#60748b', '#744700', '#c8d579', '#5f8fba', '#3e6073', '#8e6dc1', '#473785',
    '#9158a9', '#7f3f7a', '#583e84', '#073763', '#20124d', '#3d124d', '#64113f', '#780448', '#003e23', '#108764', '#1a4c3d',
    '#c8d77f', '#11888b', '#1a95bd', '#0d6c7b', '#25b56e', '#006e3d'];

const getLabelColor = (name, id) => {
    if (!name) return '#60748b';
    const str = `${name}${id || ''}`;
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
    return LABEL_COLORS[Math.abs(hash) % LABEL_COLORS.length];
};

const getContrastText = (hexColor) => {
    const r = parseInt(hexColor.substring(1, 3), 16);
    const g = parseInt(hexColor.substring(3, 5), 16);
    const b = parseInt(hexColor.substring(5, 7), 16);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq <= 128 ? 'white' : 'black';
};

export default function AssignedTicketsPanel({ tickets, loading, error, onRetry, onTicketClick, labelsByTicketId }) {
    const className = 'assignedTicketsPanel';

    if (loading) {
        return (
            <div className={className}>
                {[0, 1, 2, 3].map((i) => (
                    <div key={i} className={className + '__skeletonRow'}>
                        <Skeleton variant="text" width={72} height={16} />
                        <Skeleton variant="text" width="100%" height={16} style={{ flex: 1 }} />
                        <Skeleton variant="rounded" width={56} height={20} />
                        <Skeleton variant="rounded" width={64} height={20} />
                    </div>
                ))}
            </div>
        );
    }

    if (error) {
        return (
            <div className={className + '__state'}>
                <Inbox className={className + '__state__icon'} />
                <p className={className + '__state__text'}>Couldn't load your assigned tickets.</p>
                {onRetry && (
                    <button type="button" className={className + '__state__retry'} onClick={onRetry}>
                        <RefreshCw />
                        Try again
                    </button>
                )}
            </div>
        );
    }

    if (!tickets || tickets.length === 0) {
        return (
            <div className={className + '__state'}>
                <Inbox className={className + '__state__icon'} />
                <p className={className + '__state__text'}>Nothing assigned to you right now.</p>
            </div>
        );
    }

    return (
        <div className={className}>
            {tickets.map((ticket) => {
                const priorityVar = PRIORITY_COLOR_VAR[(ticket.priority || '').toLowerCase()];
                const labels = labelsByTicketId?.get(Number(ticket.id)) || [];
                return (
                    <div
                        key={ticket.id}
                        className={className + '__row'}
                        role="button"
                        tabIndex={0}
                        onClick={() => onTicketClick?.(ticket)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                onTicketClick?.(ticket);
                            }
                        }}
                    >
                        <div className={className + '__row__ref'}>{ticket.ticketId}</div>
                        <div className={className + '__row__title'} title={ticket.ticketTitle}>
                            {ticket.ticketTitle}
                        </div>
                        <div
                            className={className + '__row__priority'}
                            style={priorityVar ? { '--priority-color': `var(${priorityVar})` } : undefined}
                        >
                            {ticket.priority && (
                                <>
                                    <span className={className + '__row__priority__dot'} />
                                    {formatPriority(ticket.priority)}
                                </>
                            )}
                        </div>
                        <div
                            className={className + '__row__status'}
                            style={{ '--status-color': ticket.statusColor || 'var(--dbp-text-muted)' }}
                        >
                            <span className={className + '__row__status__dot'} />
                            {ticket.statusTitle}
                        </div>
                        {labels.length > 0 && (
                            <div className={className + '__row__labels'}>
                                {labels.map((label) => {
                                    const bg = getLabelColor(label.name, label.id);
                                    return (
                                        <span
                                            key={label.id}
                                            className={className + '__row__labels__chip'}
                                            style={{ background: bg, color: getContrastText(bg) }}
                                        >
                                            {label.name}
                                        </span>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
